/**
 * Wizard drafts.
 *
 * Both multi-step flows would otherwise lose everything on a refresh, and the
 * join wizard in particular is filled on a phone right after opening a link.
 * Drafts are keyed per share token so joining two events does not mix them up.
 *
 * A draft also records the account that wrote it. The storage is the browser, not
 * the account, so without that owner a different account signing in on the same
 * browser would open the wizard pre-filled with somebody else's answers - and, for
 * the join wizard, with their departure point.
 */

import { useCallback } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { LIMITS } from '@shared/constants'
import { isActivityKey, normalizeActivities, type ActivityKey } from '@shared/activities'
import { isCityKey, type CityKey } from '@shared/cities'
import { DEFAULT_TRAVEL_MODES, isTravelMode, type TravelMode } from '@shared/travel'
import {
  BUDGET_LEVELS,
  WEATHER_CONDITIONS,
  type BudgetLevel,
  type ParticipantDto,
  type WeatherCondition,
} from '@shared/types'
import { isGcj02Coordinate } from '@shared/validation'

import { useSessionStore } from './session'

/**
 * Version of the persisted draft shape.
 *
 * A payload written by another version is dropped instead of merged: a draft
 * restored with missing or retyped fields would render answers the page cannot
 * trust, and the user can only recover by clearing site data.
 */
export const DRAFT_STORAGE_VERSION = 1

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string'
}

function isNullableFiniteNumber(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value))
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function isActivityList(value: unknown): value is ActivityKey[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string' && isActivityKey(item))
  )
}

function isTravelModeList(value: unknown): value is TravelMode[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string' && isTravelMode(item))
}

function isWeatherCondition(value: unknown): value is WeatherCondition {
  return (
    typeof value === 'string' && (WEATHER_CONDITIONS as readonly string[]).includes(value)
  )
}

function isBudgetLevel(value: unknown): value is BudgetLevel {
  return typeof value === 'string' && (BUDGET_LEVELS as readonly string[]).includes(value)
}

/** Structural check of a stored organizer draft. */
function isEventDraft(value: unknown): value is EventDraft {
  if (!isRecord(value)) {
    return false
  }
  return (
    typeof value.title === 'string' &&
    typeof value.city === 'string' &&
    isCityKey(value.city) &&
    isActivityList(value.activities) &&
    isActivityList(value.optionalActivities) &&
    typeof value.eventDateStart === 'string' &&
    isNullableString(value.eventDateEnd) &&
    isWeatherCondition(value.weatherCondition)
  )
}

/** Structural check of a stored join draft. */
function isJoinDraft(value: unknown): value is JoinDraft {
  if (!isRecord(value)) {
    return false
  }
  return (
    typeof value.nickname === 'string' &&
    isFiniteNumber(value.headcount) &&
    isNullableString(value.anchorId) &&
    typeof value.locationLabel === 'string' &&
    isNullableFiniteNumber(value.locationLat) &&
    isNullableFiniteNumber(value.locationLng) &&
    isNullableString(value.travelGroupId) &&
    isTravelModeList(value.modes) &&
    isNullableString(value.availableTimeStart) &&
    isNullableString(value.availableTimeEnd) &&
    isActivityList(value.attendingActivities) &&
    isFiniteNumber(value.maxTravelMinutes) &&
    isBudgetLevel(value.budget) &&
    typeof value.note === 'string' &&
    Array.isArray(value.dietaryAvoid) &&
    Array.isArray(value.wants) &&
    typeof value.selfArrangeAllowed === 'boolean'
  )
}

export interface EventDraft {
  title: string
  city: CityKey
  activities: ActivityKey[]
  /** Declared activities the organizer marked as nice to have. */
  optionalActivities: ActivityKey[]
  eventDateStart: string
  eventDateEnd: string | null
  weatherCondition: WeatherCondition
}

export const EMPTY_EVENT_DRAFT: EventDraft = {
  title: '',
  city: 'shanghai',
  activities: [],
  optionalActivities: [],
  eventDateStart: '',
  eventDateEnd: null,
  weatherCondition: 'unknown',
}

interface EventDraftState {
  draft: EventDraft
  /** Account the stored draft belongs to, null while nobody has written one. */
  ownerId: string | null
  update: (ownerId: string, patch: Partial<EventDraft>) => void
  reset: () => void
}

export const useEventDraftStore = create<EventDraftState>()(
  persist(
    (set) => ({
      draft: EMPTY_EVENT_DRAFT,
      ownerId: null,
      // Writing under a different account starts from an empty draft: the stored
      // answers belong to the previous organizer.
      update: (ownerId, patch) =>
        set((state) => ({
          ownerId,
          draft: {
            ...(state.ownerId === ownerId ? state.draft : EMPTY_EVENT_DRAFT),
            ...patch,
          },
        })),
      reset: () => set({ draft: EMPTY_EVENT_DRAFT, ownerId: null }),
    }),
    {
      name: 'toh.draft.event',
      version: DRAFT_STORAGE_VERSION,
      // Only the data half is rebuilt here; `persist` merges the actions back in
      // from the initial state.
      migrate: (persisted, version) =>
        migrateEventDraft(persisted, version) as EventDraftState,
    },
  ),
)

/**
 * Rebuilds the persisted organizer draft.
 *
 * An older version, a foreign payload or a draft whose fields no longer match
 * the shape all produce the empty draft: nothing is half restored. Exported so
 * the migration itself can be tested without rehydrating a store.
 */
export function migrateEventDraft(
  persisted: unknown,
  version: number,
): Pick<EventDraftState, 'draft' | 'ownerId'> {
  if (version !== DRAFT_STORAGE_VERSION || !isRecord(persisted)) {
    return { draft: EMPTY_EVENT_DRAFT, ownerId: null }
  }
  const { draft, ownerId } = persisted
  if (!isEventDraft(draft) || !(ownerId === null || typeof ownerId === 'string')) {
    return { draft: EMPTY_EVENT_DRAFT, ownerId: null }
  }
  return { draft, ownerId }
}

export interface EventDraftHandle {
  draft: EventDraft
  update: (patch: Partial<EventDraft>) => void
  reset: () => void
}

/**
 * The organizer wizard's draft for the signed-in account.
 *
 * A draft written by another account is invisible rather than merely unowned: the
 * page renders the empty draft, and the first write replaces the stored one.
 */
export function useEventDraft(): EventDraftHandle {
  const userId = useSessionStore((state) => state.user?.id ?? null)
  const stored = useEventDraftStore((state) => state.draft)
  const ownerId = useEventDraftStore((state) => state.ownerId)
  const write = useEventDraftStore((state) => state.update)
  const reset = useEventDraftStore((state) => state.reset)

  const update = useCallback(
    (patch: Partial<EventDraft>) => {
      if (userId !== null) {
        write(userId, patch)
      }
    },
    [userId, write],
  )

  return {
    draft: userId !== null && ownerId === userId ? stored : EMPTY_EVENT_DRAFT,
    update,
    reset,
  }
}

export interface JoinDraft {
  nickname: string
  /**
   * People this registration stands for.
   *
   * One account may register a household, and the number is what makes a vote
   * weigh what it should, so it is asked for rather than assumed.
   */
  headcount: number
  /**
   * Departure point the member picked, when they picked one.
   *
   * A picked anchor is preferred over raw coordinates because it is a named place
   * other members can find too. Coordinates stay for the member who wants their
   * exact spot, and only one of the two is filled at a time.
   */
  anchorId: string | null
  locationLabel: string
  locationLat: number | null
  locationLng: number | null
  /**
   * Existing departure point the member chose to travel with.
   *
   * Null means they depart on their own, which is the default: merging is the
   * member's declaration rather than something the system decides.
   */
  travelGroupId: string | null
  modes: TravelMode[]
  availableTimeStart: string | null
  availableTimeEnd: string | null
  attendingActivities: ActivityKey[]
  maxTravelMinutes: number
  budget: BudgetLevel
  note: string
  /**
   * Requirements the member will not compromise on.
   *
   * Kept as plain tags rather than a nested object because the form binds them to
   * chips, and an empty list is what a member who skips the step gets.
   */
  dietaryAvoid: string[]
  /** Brands, venues or cuisines the member asked for. */
  wants: string[]
  /** Whether the member will take a taxi when the trip is over their limit. */
  selfArrangeAllowed: boolean
}

export function emptyJoinDraft(): JoinDraft {
  return {
    nickname: '',
    headcount: 1,
    anchorId: null,
    travelGroupId: null,
    locationLabel: '',
    locationLat: null,
    locationLng: null,
    modes: [...DEFAULT_TRAVEL_MODES],
    availableTimeStart: null,
    availableTimeEnd: null,
    attendingActivities: [],
    maxTravelMinutes: LIMITS.defaultMaxTravelMinutes,
    budget: 'any',
    note: '',
    dietaryAvoid: [],
    wants: [],
    selfArrangeAllowed: false,
  }
}

/** Seeds a draft from an existing participation so editing is pre-filled. */
export function joinDraftFromParticipant(participant: ParticipantDto): JoinDraft {
  return {
    nickname: participant.nickname,
    headcount: Math.max(1, participant.headcount),
    // An edit keeps the member's group, which is what carries the departure
    // point: the coordinates they once typed are not stored anywhere.
    anchorId: null,
    travelGroupId: participant.travelGroupId,
    locationLabel: participant.departureLabel ?? '',
    locationLat: null,
    locationLng: null,
    modes: participant.modes,
    availableTimeStart: participant.availableTimeStart,
    availableTimeEnd: participant.availableTimeEnd,
    attendingActivities: participant.attendingActivities,
    maxTravelMinutes: participant.maxTravelMinutes,
    budget: participant.budget,
    note: participant.note ?? '',
    dietaryAvoid: participant.constraints?.avoid ?? [],
    wants: participant.wants ?? [],
    selfArrangeAllowed: participant.selfArrange?.allowed ?? false,
  }
}

/**
 * Whether the draft names a departure point.
 *
 * A picked anchor, a kept group and typed coordinates all do; a member whose
 * coordinates were never stored relies on their group.
 */
export function hasDeparture(draft: JoinDraft): boolean {
  if (draft.anchorId !== null || draft.travelGroupId !== null) {
    return true
  }
  return (
    draft.locationLat !== null &&
    draft.locationLng !== null &&
    isGcj02Coordinate(draft.locationLat, draft.locationLng)
  )
}

/** One share token's draft together with the account that wrote it. */
export interface JoinDraftEntry {
  ownerId: string
  draft: JoinDraft
}

interface JoinDraftState {
  entries: Record<string, JoinDraftEntry>
  update: (shareToken: string, ownerId: string, patch: Partial<JoinDraft>) => void
  reset: (shareToken: string) => void
  clear: () => void
}

export const useJoinDraftStore = create<JoinDraftState>()(
  persist(
    (set) => ({
      entries: {},
      // A patch from a different account starts from an empty draft, so the previous
      // member's departure point can never be inherited.
      update: (shareToken, ownerId, patch) =>
        set((state) => {
          const current = state.entries[shareToken]
          const base = current && current.ownerId === ownerId ? current.draft : emptyJoinDraft()
          return {
            entries: {
              ...state.entries,
              [shareToken]: { ownerId, draft: { ...base, ...patch } },
            },
          }
        }),
      reset: (shareToken) =>
        set((state) => {
          const next = { ...state.entries }
          delete next[shareToken]
          return { entries: next }
        }),
      clear: () => set({ entries: {} }),
    }),
    {
      name: 'toh.draft.join',
      version: DRAFT_STORAGE_VERSION,
      migrate: (persisted, version) => migrateJoinDrafts(persisted, version) as JoinDraftState,
    },
  ),
)

/**
 * Rebuilds the persisted join drafts.
 *
 * Entries that do not survive the structural check are dropped individually: a
 * draft for one share token is independent of the others, so a single bad entry
 * must not cost the member every draft they had. Exported so the migration
 * itself can be tested without rehydrating a store.
 */
export function migrateJoinDrafts(
  persisted: unknown,
  version: number,
): Pick<JoinDraftState, 'entries'> {
  if (version !== DRAFT_STORAGE_VERSION || !isRecord(persisted) || !isRecord(persisted.entries)) {
    return { entries: {} }
  }
  const entries: Record<string, JoinDraftEntry> = {}
  for (const [shareToken, value] of Object.entries(persisted.entries)) {
    if (isRecord(value) && typeof value.ownerId === 'string' && isJoinDraft(value.draft)) {
      entries[shareToken] = { ownerId: value.ownerId, draft: value.draft }
    }
  }
  return { entries }
}

/** Normalises the activity list of a draft before submitting. */
export function draftActivities(draft: JoinDraft, fallback: ActivityKey[]): ActivityKey[] {
  const selected = normalizeActivities(draft.attendingActivities)
  return selected.length > 0 ? selected : fallback
}

/**
 * Drops every wizard draft.
 *
 * Called when the account changes or signs out: the drafts live in the browser, so
 * leaving them behind hands the next account the previous one's answers.
 */
export function clearAllDrafts(): void {
  useEventDraftStore.getState().reset()
  useJoinDraftStore.getState().clear()
  void useEventDraftStore.persist.clearStorage()
  void useJoinDraftStore.persist.clearStorage()
}
